import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Typography,
  Box,
  alpha,
} from '@mui/material';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmColor?: 'error' | 'primary' | 'warning' | 'success';
  isLoading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

const ICON_BG: Record<string, string> = {
  error:   '#FEF2F2',
  primary: '#EFF6FF',
  warning: '#FFFBEB',
  success: '#ECFDF5',
};

const ICON_COLOR: Record<string, string> = {
  error:   '#DC2626',
  primary: '#2563EB',
  warning: '#D97706',
  success: '#059669',
};

export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  confirmColor = 'error',
  isLoading = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const iconBg    = ICON_BG[confirmColor] ?? ICON_BG.error;
  const iconColor = ICON_COLOR[confirmColor] ?? ICON_COLOR.error;

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ textAlign: 'center', pt: 4, pb: 1 }}>
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: '16px',
            bgcolor: iconBg,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2,
            boxShadow: `0 4px 16px ${alpha(iconColor, 0.2)}`,
          }}
        >
          <WarningRoundedIcon sx={{ fontSize: 28, color: iconColor }} />
        </Box>
        <Typography variant="h6" fontWeight={700}>{title}</Typography>
      </DialogTitle>
      <DialogContent sx={{ textAlign: 'center', pb: 1 }}>
        <Typography variant="body2" color="text.secondary" lineHeight={1.7}>
          {message}
        </Typography>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 3, py: 2.5, justifyContent: 'center', gap: 1.5 }}>
        <Button
          onClick={onCancel}
          disabled={isLoading}
          variant="outlined"
          color="inherit"
          sx={{ minWidth: 110, borderColor: 'divider', color: 'text.primary' }}
        >
          {cancelLabel}
        </Button>
        <Button
          onClick={onConfirm}
          color={confirmColor}
          variant="contained"
          disabled={isLoading}
          startIcon={isLoading ? <CircularProgress size={15} color="inherit" /> : undefined}
          sx={{ minWidth: 110 }}
        >
          {isLoading ? 'Processing…' : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
